import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { SwapWidget } from '@relayprotocol/relay-kit-ui';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useWalletClient, useAccount, usePublicClient } from 'wagmi';
import { adaptViemWallet } from '@relayprotocol/relay-sdk';
import { logMavrkPayTransaction } from '../../utils/supabase';
import { formatUnits } from 'viem';
import RecentDeposits from './RecentDeposits';

interface DepositTabProps {
  onSuccess: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoBox = styled.div`
  padding: 12px 16px;
  background: rgba(168, 85, 247, 0.08);
  border: 1px solid rgba(168, 85, 247, 0.2);
  border-radius: 10px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
`;

const WidgetContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
`;

// USDG on Ink (locked to)
const USDG_INK = {
  chainId: 57073,
  address: '0xe343167631d89B6Ffc58B88d6b7fB0228795491D',
  decimals: 6,
  name: 'USDG',
  symbol: 'USDG',
  logoURI: 'https://mavrk.ink/GDN_USDG_Token.png'
};

const USDG_ABI = [
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const;

const DepositTab: React.FC<DepositTabProps> = ({ onSuccess }) => {
  const { openConnectModal } = useConnectModal();
  const { data: walletClient } = useWalletClient();
  const { address } = useAccount();
  const publicClient = usePublicClient({ chainId: 57073 });
  const [toToken] = useState(USDG_INK); // Locked to USDG
  const balanceBeforeSwap = useRef<bigint | null>(null);

  return (
    <Container>
      <WidgetContainer>
        <SwapWidget
          wallet={walletClient ? adaptViemWallet(walletClient) : undefined}
          supportedWalletVMs={['evm']}
          toToken={toToken}
          setToToken={() => {}} // No-op, locked
          lockToToken={true}
          onConnectWallet={openConnectModal}
          onAnalyticEvent={async (eventName, data) => {
            console.log('[MavrkPay] Relay Analytics:', eventName, data);
            
            // Capture balance before swap starts
            if (eventName === 'swap_executing' && address && publicClient) {
              try {
                const balance = await publicClient.readContract({
                  address: USDG_INK.address as `0x${string}`,
                  abi: USDG_ABI,
                  functionName: 'balanceOf',
                  args: [address],
                });
                balanceBeforeSwap.current = balance;
                console.log('[MavrkPay] Balance before swap:', formatUnits(balance, 6), 'USDG');
              } catch (err) {
                console.error('[MavrkPay] Error fetching balance:', err);
              }
            }
          }}
          onSwapSuccess={async (data) => {
            console.log('[MavrkPay] Deposit successful:', data);
            
            // Calculate deposit amount from balance change
            if (address && publicClient && balanceBeforeSwap.current !== null) {
              try {
                // Wait a moment for balance to update
                await new Promise(resolve => setTimeout(resolve, 2000));
                
                const balanceAfter = await publicClient.readContract({
                  address: USDG_INK.address as `0x${string}`,
                  abi: USDG_ABI,
                  functionName: 'balanceOf',
                  args: [address],
                });

                const depositAmount = balanceAfter - balanceBeforeSwap.current;
                const depositAmountFormatted = formatUnits(depositAmount, 6);
                
                console.log('[MavrkPay] Deposited amount:', depositAmountFormatted, 'USDG');

                // Log deposit to Supabase with actual amount
                await logMavrkPayTransaction(
                  address,
                  'deposit',
                  depositAmountFormatted, // Actual USDG amount deposited
                  undefined, // No counterparty for deposits
                  undefined, // tx hash not readily available from widget
                  undefined
                );
                
                balanceBeforeSwap.current = null; // Reset
              } catch (err) {
                console.error('[MavrkPay] Error logging deposit:', err);
              }
            }
            
            onSuccess(); // Refresh balance
          }}
          onSwapError={(error) => {
            console.error('[MavrkPay] Deposit error:', error);
          }}
        />
      </WidgetContainer>

      <RecentDeposits userAddress={address} />
    </Container>
  );
};

export default DepositTab;

